const MIN_DESCENDANTS_TO_RASTERIZE = 100;
const CHART_CONTAINER_SELECTOR = ".highcharts-container";
const INLINED_TEXT_STYLE_PROPERTIES = ["font-family", "font-size", "font-weight", "font-style", "fill", "opacity"];

function isLargeStandaloneSvg(svg: SVGSVGElement): boolean {
  if (svg.parentElement?.closest("svg") || svg.closest(CHART_CONTAINER_SELECTOR)) {
    return false;
  }
  return svg.querySelectorAll("*").length >= MIN_DESCENDANTS_TO_RASTERIZE;
}

function serializeSvgWithTextStyles(svg: SVGSVGElement, width: number, height: number): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  // The image can not see the page CSS, so the text styles the page applied are inlined.
  const sourceTexts = svg.querySelectorAll("text, tspan");
  const clonedTexts = clone.querySelectorAll("text, tspan");
  sourceTexts.forEach((sourceText, index) => {
    const computed = window.getComputedStyle(sourceText);
    const clonedText = clonedTexts[index] as SVGElement | undefined;
    for (const property of INLINED_TEXT_STYLE_PROPERTIES) {
      clonedText?.style.setProperty(property, computed.getPropertyValue(property));
    }
  });
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  return new XMLSerializer().serializeToString(clone);
}

/**
 * html-to-image clones an SVG node by node and reads the full computed style of each one. Legend
 * SVGs (one rect per color step) therefore cost seconds. While `run` executes, such SVGs are
 * swapped for a single `<img>` that shows the same picture and are restored afterwards.
 * Highcharts charts are left alone.
 */
export async function withRasterizedSvgs<T>(root: HTMLElement, run: () => Promise<T>): Promise<T> {
  const swaps: Array<{ svg: SVGSVGElement; image: HTMLImageElement }> = [];

  try {
    for (const svg of Array.from(root.querySelectorAll("svg"))) {
      if (!isLargeStandaloneSvg(svg)) {
        continue;
      }
      const { width, height } = svg.getBoundingClientRect();
      if (width <= 0 || height <= 0) {
        continue;
      }

      const image = document.createElement("img");
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        serializeSvgWithTextStyles(svg, width, height),
      )}`;
      image.style.cssText = `display:block;flex:none;width:${width}px;height:${height}px;`;
      svg.replaceWith(image);
      swaps.push({ svg, image });
    }

    await Promise.all(swaps.map(({ image }) => image.decode?.().catch(() => undefined)));
    return await run();
  } finally {
    for (const { svg, image } of swaps) {
      image.replaceWith(svg);
    }
  }
}
